import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TrashIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, ConfirmModal, RowAction, Select, Table } from "../ui";
import { abroadReviewApi } from "../../api/abroadLoans";
import { errorMessageOf, scholarshipsApi } from "../../api/scholarships";
import { canManageScholarships } from "../scholarships/access";
import { SCHOLARSHIPS_KEY, formatKigaliDate, fullName } from "../scholarships/model";
import { committeeKey } from "./review";

// HEC evaluator accounts are shared with scholarships; their principal ID is the abroad evaluator ID
const ACCOUNTS_KEY = [...SCHOLARSHIPS_KEY, "evaluator-accounts"];
const accountLabel = (a) => (a.email && fullName(a) !== a.email ? `${fullName(a)} (${a.email})` : fullName(a));

/**
 * Who scores this loan call's applications. Every member scores every submitted application, unless
 * they declare a conflict of interest on it. Removing a member discards their drafts and keeps what
 * they submitted.
 */
export default function CommitteeTab({ call }) {
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState("");
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);

  const { data: members = [], isPending } = useQuery({
    queryKey: committeeKey(call.id),
    queryFn: async () => (await abroadReviewApi.committee(call.id)).data,
  });
  const editable = canManageScholarships() && call.state !== "CANCELLED";
  const { data: accounts = [] } = useQuery({
    queryKey: ACCOUNTS_KEY,
    enabled: editable,
    queryFn: async () => (await scholarshipsApi.get("admin/evaluators", { params: { page: 0, size: 100 } })).data?.content ?? [],
  });

  const memberIds = new Set(members.map((m) => m.evaluatorId));
  const options = accounts.filter((a) => !memberIds.has(a.principalId)).map((a) => ({ value: a.principalId, label: accountLabel(a) }));
  const refresh = () => queryClient.invalidateQueries({ queryKey: committeeKey(call.id) });

  const assign = async () => {
    setBusy(true);
    try {
      await abroadReviewApi.assign(call.id, chosen);
      toast.success("Evaluator added to the committee");
      setChosen("");
      await refresh();
    } catch (error) {
      console.error(`Failed to add evaluator ${chosen} to abroad loan call ${call.id}:`, error);
      toast.error(errorMessageOf(error, "Failed to add the evaluator. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await abroadReviewApi.unassign(call.id, removing.evaluatorId);
      toast.success(`${fullName(removing)} removed`);
      setRemoving(null);
      await refresh();
    } catch (error) {
      console.error(`Failed to remove evaluator ${removing.evaluatorId} from abroad loan call ${call.id}:`, error);
      toast.error(errorMessageOf(error, "Failed to remove the evaluator. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: "name", label: "Evaluator", accessor: fullName, render: (m) => <span className="font-medium text-slate-800">{fullName(m)}</span> },
    { key: "assignedAt", label: "Added", accessor: (m) => formatKigaliDate(m.assignedAt), sortValue: (m) => m.assignedAt },
    ...(editable ? [{ key: "remove", label: "", exportable: false, render: (m) => <RowAction Icon={TrashIcon} label="Remove" variant="ghost" onClick={() => setRemoving(m)} disabled={busy} /> }] : []),
  ];

  return (
    <div className="flex max-w-3xl flex-col gap-5">
      {editable && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <Select label="Add an evaluator" placeholder="Choose HEC staff" options={options} value={chosen} onChange={(e) => setChosen(e.target.value)} className="w-full sm:flex-1" disabled={busy} />
          <Button icon={<UserPlusIcon className="h-4 w-4" />} onClick={assign} disabled={!chosen} loading={busy && Boolean(chosen)}>
            Add
          </Button>
        </div>
      )}
      <Table columns={columns} data={members} isLoading={isPending} searchable={false} exportable={false} emptyTitle="No evaluators on the committee yet" />
      <ConfirmModal
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        loading={busy}
        destructive
        title={`Remove ${removing ? fullName(removing) : ""}?`}
        description="Marks they have not submitted are discarded; submitted ones still count towards the scores."
        confirmLabel="Remove"
      />
    </div>
  );
}
